/**
 * Cores de referência dos partidos (aproximação das cores oficiais, ajustadas para fundo escuro)
 * e ordem usada apenas para desenhar as bancadas no hemiciclo da esquerda para a direita.
 *
 * A ordem do desenho é editorial e aproximada; a posição analítica dos partidos no espectro
 * virá dos próprios votos (Mapa do Congresso / Bússola), não desta lista.
 */

const CORES: Record<string, string> = {
  PT: '#e5273a',
  PL: '#2f6fd6',
  PSOL: '#f5c518',
  PCDOB: '#c81d25',
  PV: '#2fa84f',
  REDE: '#1aa39a',
  PDT: '#f26b38',
  PSB: '#f2b705',
  SOLIDARIEDADE: '#ff8a3d',
  AVANTE: '#f0a500',
  CIDADANIA: '#e84393',
  PSDB: '#3b82f6',
  MDB: '#2e8b57',
  PSD: '#f39c12',
  PODE: '#2bb3a3',
  PODEMOS: '#2bb3a3',
  UNIÃO: '#5b7cfa',
  UNIAO: '#5b7cfa',
  PP: '#1f4fd1',
  REPUBLICANOS: '#3c82c4',
  NOVO: '#ff7a00',
  PRD: '#6c7bd9',
  DC: '#8e9aaf',
  PMB: '#b57edc',
  PRTB: '#7f8c8d',
  AGIR: '#95a5a6',
  MOBILIZA: '#a3b18a',
  'S.PART.': '#6b7280',
  // Partidos das legislaturas 55 e 56 (2015–2023) extintos por fusão ou incorporação.
  DEM: '#4f86c6',
  PSL: '#1e5aa8',
  PTB: '#d97706',
  PSC: '#2f855a',
  PROS: '#f97316',
  PATRIOTA: '#15803d',
  PHS: '#0e7490',
  PRP: '#9f1239',
  PPL: '#b91c1c',
  // Siglas antigas dos renomeados aparecem só em payloads brutos; a cor acompanha a sigla atual.
  PMDB: '#2e8b57',
  PR: '#2f6fd6',
  PRB: '#3c82c4',
  PTN: '#2bb3a3',
  PPS: '#e84393',
  PEN: '#15803d',
  PTDOB: '#f0a500',
}

/** Ordem aproximada esquerda → direita para o desenho do hemiciclo. */
export const ORDEM_HEMICICLO: string[] = [
  'PSOL',
  'PCDOB',
  'PT',
  'PV',
  'REDE',
  'PDT',
  'PSB',
  'SOLIDARIEDADE',
  'PROS',
  'AVANTE',
  'CIDADANIA',
  'PSDB',
  'MDB',
  'PSD',
  'PODE',
  'PODEMOS',
  'PHS',
  'PTB',
  'UNIÃO',
  'UNIAO',
  'DEM',
  'PRD',
  'PATRIOTA',
  'PSC',
  'PP',
  'REPUBLICANOS',
  'PSL',
  'PL',
  'NOVO',
]

/** Maiúsculas, sem acentos e sem espaços nas pontas ("União Brasil" → "UNIAO BRASIL", "PCdoB" → "PCDOB"). */
export function normalizarSigla(sigla: string | null | undefined): string {
  return (sigla ?? '')
    .trim()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toUpperCase()
    .replace('PC DO B', 'PCDOB')
}

/** Cor determinística para siglas fora da tabela. */
function corPorHash(texto: string): string {
  let h = 0
  for (let i = 0; i < texto.length; i++) {
    h = (h * 31 + texto.charCodeAt(i)) >>> 0
  }
  const hue = h % 360
  return `hsl(${hue} 55% 60%)`
}

export function corDoPartido(sigla: string | null | undefined, corHexApi?: string | null): string {
  if (corHexApi) return corHexApi
  const chave = normalizarSigla(sigla)
  if (!chave) return CORES['S.PART.']
  return CORES[chave] ?? corPorHash(chave)
}

export function posicaoNoHemiciclo(sigla: string | null | undefined): number {
  const chave = normalizarSigla(sigla)
  const idx = ORDEM_HEMICICLO.indexOf(chave)
  // Desconhecidos vão para o centro.
  return idx === -1 ? ORDEM_HEMICICLO.length / 2 : idx
}

/** Ordena siglas para o desenho: ordem editorial e, em empate/desconhecidos, por tamanho da bancada. */
export function ordenarBancadas<T extends { partido: string | null | undefined; cadeiras: number }>(bancadas: T[]): T[] {
  return [...bancadas].sort((a, b) => {
    const pa = posicaoNoHemiciclo(a.partido)
    const pb = posicaoNoHemiciclo(b.partido)
    if (pa !== pb) return pa - pb
    return b.cadeiras - a.cadeiras
  })
}
